import { RANGES, settings, updateSettings } from "../../../settings";
import type { Settings } from "../../../settings";
import { Segmented } from "../../components/Segmented";
import type { SegmentedOption } from "../../components/Segmented";
import { Slider } from "../../components/Slider";
import { Toggle } from "../../components/Toggle";

type AudioSettings = Settings["audio"];
type VolumeKey = "master" | "sfx" | "ui" | "music";

const VOLUMES: readonly { key: VolumeKey; label: string; description: string }[] = [
  { key: "master", label: "Master volume", description: "Scales everything below." },
  { key: "sfx", label: "Effects volume", description: "Weapons, footsteps, impacts." },
  { key: "ui", label: "Interface volume", description: "Menu and HUD sounds." },
  { key: "music", label: "Music volume", description: "Menu and match music." },
];

const HITSOUND_OPTIONS: readonly SegmentedOption<AudioSettings["hitsound"]>[] = [
  { value: "classic", label: "Classic" },
  { value: "tick", label: "Tick" },
  { value: "off", label: "Off" },
];

/** Stored volumes are 0..1, the sliders show whole percent. */
const PERCENT = {
  min: Math.round(RANGES.volume.min * 100),
  max: Math.round(RANGES.volume.max * 100),
  step: Math.round(RANGES.volume.step * 100),
} as const;

export function AudioTab() {
  const a = settings.value.audio;

  const setVolume = (key: VolumeKey, percent: number) => {
    const patch: Partial<Record<VolumeKey, number>> = {};
    patch[key] = percent / 100;
    updateSettings({ audio: patch });
  };

  return (
    <div class="settings__content">
      <h3 class="settings__group">Volume</h3>
      <div class="settings__rows">
        {VOLUMES.map((v) => (
          <Slider
            key={v.key}
            label={v.label}
            description={v.description}
            unit="%"
            {...PERCENT}
            value={Math.round(a[v.key] * 100)}
            onChange={(percent) => setVolume(v.key, percent)}
          />
        ))}
      </div>

      <h3 class="settings__group">Combat feedback</h3>
      <div class="settings__rows">
        <Segmented
          label="Hit sound"
          description="Played when your shot hits an enemy: a body hit and a headshot sound different."
          value={a.hitsound}
          options={HITSOUND_OPTIONS}
          onChange={(hitsound) => updateSettings({ audio: { hitsound } })}
        />
        <Toggle
          label="Kill sound"
          description="Plays an extra cue when you eliminate an enemy; a headshot kill has its own."
          checked={a.killsound}
          onChange={(killsound) => updateSettings({ audio: { killsound } })}
        />
      </div>
    </div>
  );
}
